/** @jsxImportSource @emotion/react */
import { useContext } from 'react';

import { formatPrice } from '@common/utlis';
import { RadioButtonWithCount } from '@stories/atoms/FormElements/RadioButton';
import { PageContext } from '@stories/templates/Context/pageContext';
import { isValidOption } from '@stories/templates/Search/Filters/Filters';
import { FORMAT_CURRENCY_OPTIONS } from '@utils/constants';
import { isSortingOrder, useProductListContext } from '@utils/productList';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';
import type { TFilterSection } from '@stories/@types/custom';

// import {
//   sendGAEvent,
//   convertValueToIntegers,
// } from '../../../../../../common/helpers';

// const convertValueToIntegers = (value) => {
//   return value
//     .split('-')
//     .map((item) => Math.abs(item))
//     .filter((item) => !Number.isNaN(item));
// };

type TSectionRadioButtonProps = {
  filterSection: TFilterSection;
  cssProps?: Interpolation<Theme>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const SectionRadioButton: React.FC<TSectionRadioButtonProps> = ({
  filterSection: { options, name },
  onChange,
  cssProps
}) => {
  const {
    reducerFunctions: { selectRadio }
  } = useProductListContext();
  const defaultOnChange = ({ target }) => {
    selectRadio({ section: target.name, option: target.value });
  };

  return (
    <div css={cssProps}>
      {options.map((option) => (
        <RadioButtonWithCount
          {...{ name, onChange: onChange || defaultOnChange, ...option }}
          cssProps={{
            display: 'block',
            margin: '0.25rem 0'
          }}
          key={option.value}
        />
      ))}
    </div>
  );
};

const SectionSorting: React.FC<TSectionRadioButtonProps> = ({ filterSection }) => {
  const {
    activeFiltersAndSorting: { currentSortingOrder },
    reducerFunctions: { setCurrentSortingOrder },
    defaultSettings: { defaultSortingOrder }
  } = useProductListContext();

  const optionsWithCheckedState = filterSection.options.map((option) => ({
    ...option,
    checked: option.value === (currentSortingOrder || defaultSortingOrder)
  }));

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentSortingOrder(isSortingOrder(event.target.value) ? event.target.value : defaultSortingOrder);
  };

  return (
    <SectionRadioButton
      filterSection={{ ...filterSection, options: optionsWithCheckedState }}
      onChange={onChange}
    />
  );
};

const SectionPrice: React.FC<TSectionRadioButtonProps> = ({ filterSection }) => {
  const {
    activeFiltersAndSorting: { activeFilters },
    productsAndAvailableFilters: { total }
  } = useProductListContext();

  const { storeCode } = useContext(PageContext);
  const formatCurrencyOptions = FORMAT_CURRENCY_OPTIONS[storeCode] || FORMAT_CURRENCY_OPTIONS['vomachterhof'];

  const optionsFormatted = filterSection.options.filter(isValidOption).map((option) => ({
    ...option,
    value: option.value === '75.0-*' ? '75' : option.value,
    label:
      option.value === '75.0-*'
        ? 'ab 75 €'
        : (option?.label || '')
            .toString()
            .trim()
            .split(/-/)
            .map((price) => formatPrice(price, { ...formatCurrencyOptions, maximumFractionDigits: 0 }))
            .join(' - '),
    checked: (activeFilters[filterSection.name] || []).includes(
      option.value === '75.0-*' ? '75' : option.value
    )
  }));

  const formattedFilterSection = {
    ...filterSection,
    options: [
      {
        checked: !activeFilters[filterSection.name] || activeFilters[filterSection.name].length === 0,
        label: 'Alle Preise',
        value: 'all',
        count: total
      },
      ...optionsFormatted
    ]
  };

  return <SectionRadioButton filterSection={formattedFilterSection} />;
};

const SectionRatings: React.FC<TSectionRadioButtonProps> = ({ filterSection }) => {
  const {
    activeFiltersAndSorting: { activeFilters },
    productsAndAvailableFilters: { total }
  } = useProductListContext();

  const filterOptionsPrepared = filterSection.options
    .map((option) => {
      const starLabel = '★';
      const starNumber = typeof option.label === 'string' ? Number(option.label.split('-')[0]) + 1 : 0;
      return {
        ...option,
        checked: (activeFilters[filterSection.name] || []).includes(option.value),
        starNumber,
        label: <span>{[...Array(starNumber)].map(() => starLabel)}</span>
      };
    })
    .filter(({ starNumber }) => starNumber > 2)
    .sort((a, b) => {
      return a.value > b.value ? -1 : 1;
    });

  const ratingFilterSection = {
    ...filterSection,
    options: [
      {
        checked: !activeFilters[filterSection.name] || activeFilters[filterSection.name].length === 0,
        label: 'Alle anzeigen',
        value: 'all',
        count: total
      },
      ...filterOptionsPrepared
    ]
  };
  return <SectionRadioButton filterSection={ratingFilterSection} />;
};

export default SectionRadioButton;
export { SectionPrice, SectionRatings, SectionSorting };
